import { useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bug, BookOpen, CheckSquare, LayoutGrid, Pencil } from "lucide-react";
import { format } from "date-fns";
import {
  useUpdateBacklogItem,
  type ProjectBacklogItem,
  type ProjectEpic,
  type ProjectSprint,
  type BacklogItemStatus,
  type CreateBacklogItemData,
} from "@/hooks/useProjectPlanning";
import { BacklogItemDialog } from "./BacklogItemDialog";
import {
  BACKLOG_STATUS_META,
  PRIORITY_TONE,
  TONE_BG_CLASS,
  TONE_TEXT_CLASS,
} from "@/lib/statusTone";

const columns: { id: BacklogItemStatus; label: string; color: string }[] = (
  ["todo", "in_progress", "review", "done"] as const
).map((id) => ({
  id,
  label: BACKLOG_STATUS_META[id].label,
  color: TONE_BG_CLASS[BACKLOG_STATUS_META[id].tone],
}));

const typeIcons = {
  task: <CheckSquare className="h-3 w-3" />,
  bug: <Bug className="h-3 w-3" />,
  story: <BookOpen className="h-3 w-3" />,
};
const priorityColors = PRIORITY_TONE.map((t) => TONE_TEXT_CLASS[t]);

export function SprintBoard({
  clientProjectId,
  items,
  epics,
  sprints,
}: {
  clientProjectId: string;
  items: ProjectBacklogItem[];
  epics: ProjectEpic[];
  sprints: ProjectSprint[];
}) {
  const updateItem = useUpdateBacklogItem(clientProjectId);
  const [editingItem, setEditingItem] = useState<ProjectBacklogItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const defaultSprintId = useMemo(
    () => sprints.find((s) => s.status === "active")?.id ?? sprints[0]?.id ?? "",
    [sprints],
  );
  const [selectedSprintId, setSelectedSprintId] = useState(defaultSprintId);
  const activeSprintId = selectedSprintId || defaultSprintId;

  const epicById = Object.fromEntries(epics.map((e) => [e.id, e]));
  const sprintItems = items.filter((i) => i.sprint_id === activeSprintId && i.status !== "backlog");

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newStatus = result.destination.droppableId as BacklogItemStatus;
    const item = items.find((i) => i.id === result.draggableId);
    if (!item || item.status === newStatus) return;
    updateItem.mutate({
      id: item.id,
      data: {
        status: newStatus,
        display_order: result.destination.index,
      } as Partial<CreateBacklogItemData> & { display_order: number },
    });
  };

  const activeSprint = sprints.find((s) => s.id === activeSprintId) ?? null;
  const totalPts = sprintItems.reduce((s, i) => s + (i.story_points ?? 0), 0);
  const donePts = sprintItems
    .filter((i) => i.status === "done")
    .reduce((s, i) => s + (i.story_points ?? 0), 0);
  const period =
    activeSprint?.start_date && activeSprint?.end_date
      ? `${format(new Date(activeSprint.start_date), "dd/MM")} – ${format(new Date(activeSprint.end_date), "dd/MM")}`
      : null;

  const openEdit = (item: ProjectBacklogItem) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  if (sprints.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        <LayoutGrid className="mx-auto mb-2 h-8 w-8 opacity-40" />
        Crie uma sprint na aba "Sprints" para usar o board.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-[15px] font-bold">
            {activeSprint?.name ?? "Sprint"}
            {period ? ` · ${period}` : ""}
          </h2>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">
            {totalPts} pts · {donePts} concluídos
          </p>
        </div>
        <div className="w-56">
          <Select value={activeSprintId} onValueChange={setSelectedSprintId}>
            <SelectTrigger className="h-9 rounded-[9px] text-[12.5px]">
              <SelectValue placeholder="Selecione a sprint" />
            </SelectTrigger>
            <SelectContent>
              {sprints.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {columns.map((col) => {
            const colItems = sprintItems
              .filter((i) => i.status === col.id)
              .sort((a, b) => a.display_order - b.display_order);
            return (
              <div key={col.id} className="min-h-[160px] rounded-lg bg-muted/40 p-3">
                <div className="mb-3 flex items-center gap-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${col.color}`} />
                  <span className="text-sm font-semibold">{col.label}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {colItems.length}
                  </Badge>
                </div>
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[60px] space-y-2 rounded transition-colors ${snapshot.isDraggingOver ? "bg-primary/5" : ""}`}
                    >
                      {colItems.map((item, index) => {
                        const epic = item.epic_id ? epicById[item.epic_id] : null;
                        return (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(drag, dragSnapshot) => (
                              <div
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                <Card
                                  className={`border shadow-none ${dragSnapshot.isDragging ? "opacity-80 shadow-lg" : ""}`}
                                >
                                  <CardContent className="space-y-1.5 p-2.5">
                                    <div className="flex items-start justify-between gap-1">
                                      <div className="flex min-w-0 items-start gap-1.5">
                                        <span className="mt-0.5 shrink-0 text-muted-foreground">
                                          {typeIcons[item.item_type]}
                                        </span>
                                        <p className="line-clamp-2 text-sm leading-snug">
                                          {item.title}
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 shrink-0"
                                        onClick={() => openEdit(item)}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      {epic && (
                                        <Badge variant="outline" className="px-1 text-[10px]">
                                          {epic.title}
                                        </Badge>
                                      )}
                                      {item.story_points != null && (
                                        <Badge variant="secondary" className="px-1 text-[10px]">
                                          {item.story_points} pts
                                        </Badge>
                                      )}
                                      {item.priority > 0 && (
                                        <span
                                          className={`text-xs ${priorityColors[item.priority]}`}
                                        >
                                          ●
                                        </span>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      <BacklogItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientProjectId={clientProjectId}
        epics={epics}
        sprints={sprints}
        item={editingItem}
      />
    </div>
  );
}
